/**
 * In-memory LRU cache for storing File objects by their ID.
 * Used to enable thumbnail display after a message is sent (within the same session).
 *
 * Max cache size of 100 files to prevent excessive memory usage.
 */
class FileCache {
	private cache = new Map<string, File>();
	private accessOrder: string[] = [];
	private readonly maxSize = 100;

	set(id: string, file: File): void {
		if (this.cache.has(id)) {
			this.accessOrder = this.accessOrder.filter((existingId) => existingId !== id);
		}

		this.cache.set(id, file);
		this.accessOrder.push(id);

		if (this.cache.size > this.maxSize) {
			const lruId = this.accessOrder.shift();
			if (lruId) {
				this.cache.delete(lruId);
			}
		}
	}

	get(id: string): File | undefined {
		const file = this.cache.get(id);

		if (file) {
			this.accessOrder = this.accessOrder.filter((existingId) => existingId !== id);
			this.accessOrder.push(id);
		}

		return file;
	}

	delete(id: string): void {
		this.cache.delete(id);
		this.accessOrder = this.accessOrder.filter((existingId) => existingId !== id);
	}

	clear(): void {
		this.cache.clear();
		this.accessOrder = [];
	}
}

// Singleton instance
export const fileCache: FileCache = new FileCache();
